import { FilterNamesEnum } from "pages/project-dashboard/models";
import { AgGridReact } from "ag-grid-react";
import { message } from "antd";
import {
  BLANKS_FILTER_KEY,
  ErrorMessages,
  ESubmittalStatus,
  EWorkflowStatusDataBlock,
  LINKED_ACTIVITIES_COLUMN_FILTER_LABELS
} from "../constants";

const getFilterAllValues = async (
  gridRef: React.RefObject<AgGridReact<any>>,
  colId: string
) => {
  const filter: any = await gridRef.current?.api.getColumnFilterInstance(colId);
  if (filter?.handler?.valueModel)
    return Array.from(filter?.handler?.valueModel?.allValues, ([key]) => key);
  return [];
};

// Helper: Create a filter applier function bound to a grid ref
const createApplyFilter =
  (gridRef: React.RefObject<AgGridReact<any>>) =>
  (colId: string, values: string[], other?: Record<string, unknown>) => {
    gridRef.current?.api?.setColumnFilterModel(colId, { values, ...other });
  };

// Helper: Apply filter excluding specific status values
const applyExcludingStatusFilter = async (
  gridRef: React.RefObject<AgGridReact<any>>,
  colId: string,
  excludeValues: string[]
) => {
  const allValues = await getFilterAllValues(gridRef, colId);
  gridRef.current?.api?.setColumnFilterModel(colId, {
    values: allValues.filter((value: string) => !excludeValues.includes(value))
  });
};

// Helper: Apply sort by next_planned_deadline ascending
const applySortByNextDeadline = (
  gridRef: React.RefObject<AgGridReact<any>>
) => {
  gridRef.current?.api?.applyColumnState({
    state: [{ colId: "next_planned_deadline", sort: "asc" }],
    defaultState: { sort: null }
  });
};

// Helper: Notify filter changed (deferred to next tick)
const notifyFilterChanged = (gridRef: React.RefObject<AgGridReact<any>>) => {
  setTimeout(() => {
    gridRef?.current?.api?.onFilterChanged();
  }, 0);
};

export async function applySubmittalLogFilters(params: {
  gridRef: React.RefObject<AgGridReact<any>>;
  loggedInUserInfo: any;
  submittalListFilter: any;
  SubmittalStatusToStrMap: any;
  additionalData?: any;
}) {
  const {
    gridRef,
    loggedInUserInfo,
    submittalListFilter,
    SubmittalStatusToStrMap,
    additionalData
  } = params;

  if (!gridRef.current) return;

  const applyFilter = createApplyFilter(gridRef);
  const userFullName = loggedInUserInfo
    ? `${loggedInUserInfo["first-name"] ?? ""} ${
        loggedInUserInfo["last-name"] ?? ""
      }`.trim()
    : "";
  const excludeCompletedStatuses = [
    SubmittalStatusToStrMap[ESubmittalStatus.DONE],
    SubmittalStatusToStrMap[ESubmittalStatus.VOID]
  ];

  switch (submittalListFilter.key) {
    // Architect dashboard filters
    case "arch-due-in-days":
      applyFilter("planned_ext_review_completed_date", ["Due in 5 days"]);
      applyFilter("state", ["Draft", "Open"]);
      break;
    case "arch-overdue":
      applyFilter("planned_ext_review_completed_date", ["Overdue"]);
      applyFilter("state", ["Draft", "Open"]);
      break;
    case "arch-to-review":
      if (!userFullName) {
        message.error(ErrorMessages.SomethingWentWrong);
        return;
      }
      applyFilter("assignee", [userFullName]);
      applyFilter("workflow_status", [
        SubmittalStatusToStrMap[ESubmittalStatus.EXTERNAL_REVIEW]
      ]);
      break;
    case FilterNamesEnum.DueIn2Week:
      applySortByNextDeadline(gridRef);
      applyFilter("next_planned_deadline", ["Overdue or due in 2 weeks"]);
      await applyExcludingStatusFilter(
        gridRef,
        "status",
        excludeCompletedStatuses
      );
      break;
    case FilterNamesEnum.SCDueIn2Week:
      applySortByNextDeadline(gridRef);
      applyFilter("next_planned_deadline", ["Overdue or due in 2 weeks"]);
      if (additionalData?.loggedInUserCompany) {
        applyFilter("responsible_contractor", [
          additionalData.loggedInUserCompany
        ]);
      }
      await applyExcludingStatusFilter(
        gridRef,
        "status",
        excludeCompletedStatuses
      );
      break;
    case FilterNamesEnum.HighDueIn2Week:
      applyFilter("due_date", ["Overdue or due in 2 weeks"]);
      applyFilter("risk_level", ["HIGH"]);
      await applyExcludingStatusFilter(
        gridRef,
        "status",
        excludeCompletedStatuses
      );
      break;
    case FilterNamesEnum.SCHighDueIn2Week:
      applyFilter("due_date", ["Overdue or due in 2 weeks"]);
      applyFilter("risk_level", ["HIGH"]);
      if (additionalData?.loggedInUserCompany) {
        applyFilter("responsible_contractor", [
          additionalData.loggedInUserCompany
        ]);
      }
      await applyExcludingStatusFilter(
        gridRef,
        "status",
        excludeCompletedStatuses
      );
      break;
    case FilterNamesEnum.LowDueIn2Week:
      applyFilter("due_date", ["Overdue or due in 2 weeks"]);
      applyFilter("risk_level", ["LOW", "MEDIUM"]);
      await applyExcludingStatusFilter(
        gridRef,
        "status",
        excludeCompletedStatuses
      );
      break;
    case FilterNamesEnum.SCLowDueIn2Week:
      applyFilter("due_date", ["Overdue or due in 2 weeks"]);
      applyFilter("risk_level", ["LOW", "MEDIUM"]);
      if (additionalData?.loggedInUserCompany) {
        applyFilter("responsible_contractor", [
          additionalData.loggedInUserCompany
        ]);
      }
      await applyExcludingStatusFilter(
        gridRef,
        "status",
        excludeCompletedStatuses
      );
      break;
    case FilterNamesEnum.HighDueLater:
      applyFilter("due_date", ["due after 2 weeks"]);
      applyFilter("risk_level", ["HIGH"]);
      await applyExcludingStatusFilter(
        gridRef,
        "status",
        excludeCompletedStatuses
      );
      break;
    case FilterNamesEnum.SCHighDueLater:
      applyFilter("due_date", ["due after 2 weeks"]);
      applyFilter("risk_level", ["HIGH"]);
      if (additionalData?.loggedInUserCompany) {
        applyFilter("responsible_contractor", [
          additionalData.loggedInUserCompany
        ]);
      }
      await applyExcludingStatusFilter(
        gridRef,
        "status",
        excludeCompletedStatuses
      );
      break;
    case FilterNamesEnum.LowDueLater:
      applyFilter("due_date", ["due after 2 weeks"]);
      applyFilter("risk_level", ["LOW", "MEDIUM"]);
      await applyExcludingStatusFilter(
        gridRef,
        "status",
        excludeCompletedStatuses
      );
      break;
    case FilterNamesEnum.SCLowDueLater:
      applyFilter("due_date", ["due after 2 weeks"]);
      applyFilter("risk_level", ["LOW", "MEDIUM"]);
      if (additionalData?.loggedInUserCompany) {
        applyFilter("responsible_contractor", [
          additionalData.loggedInUserCompany
        ]);
      }
      await applyExcludingStatusFilter(
        gridRef,
        "status",
        excludeCompletedStatuses
      );
      break;
    case FilterNamesEnum.AlertNewSubmittals:
      applyFilter("created_at", ["after date"], {
        dateFrom: submittalListFilter?.value
      });
      await applyExcludingStatusFilter(gridRef, "status", [
        SubmittalStatusToStrMap[ESubmittalStatus.VOID]
      ]);
      break;
    case FilterNamesEnum.NoPlan:
      applyFilter("final_deadline_date", [BLANKS_FILTER_KEY]);
      await applyExcludingStatusFilter(gridRef, "status", [
        SubmittalStatusToStrMap[ESubmittalStatus.DONE],
        SubmittalStatusToStrMap[ESubmittalStatus.VOID]
      ]);
      break;
    case FilterNamesEnum.SubmittalsNotFoundInSource:
      applyFilter("not_found_in_source", ["Missing"]);
      break;
    case FilterNamesEnum.LinkedToDeletedActivities: {
      applyFilter("linked_tasks", [
        LINKED_ACTIVITIES_COLUMN_FILTER_LABELS.DELETED
      ]);
      break;
    }
    default:
  }

  notifyFilterChanged(gridRef);
}

export async function applyMaterialLogFilters(params: {
  gridRef: React.RefObject<AgGridReact<any>>;
  materialListFilter: any;
  additionalData?: any;
}) {
  const { gridRef, materialListFilter, additionalData } = params;
  if (!gridRef.current) return;

  const applyFilter = createApplyFilter(gridRef);
  const excludeCompletedStatuses = [
    EWorkflowStatusDataBlock.WF_COMPLETED,
    EWorkflowStatusDataBlock.VOID
  ];

  switch (materialListFilter.key) {
    case FilterNamesEnum.IsLinkedVoidSubmittal:
      applyFilter("is_linked_void_submittal", ["Voided Submittals"]);
      break;
    case FilterNamesEnum.IsLinkedDeletedSubmittal:
      applyFilter("is_linked_void_submittal", ["Deleted Submittals"]);
      break;
    case FilterNamesEnum.DueIn2Week: {
      applySortByNextDeadline(gridRef);
      await applyExcludingStatusFilter(
        gridRef,
        "workflow_status",
        excludeCompletedStatuses
      );
      applyFilter("next_planned_deadline", ["Overdue or due in 2 weeks"]);
      break;
    }
    case FilterNamesEnum.SCDueIn2Week: {
      applySortByNextDeadline(gridRef);
      await applyExcludingStatusFilter(
        gridRef,
        "workflow_status",
        excludeCompletedStatuses
      );
      if (additionalData?.loggedInUserCompany) {
        applyFilter("trade_partner", [additionalData.loggedInUserCompany]);
      }
      applyFilter("next_planned_deadline", ["Overdue or due in 2 weeks"]);
      break;
    }
    case FilterNamesEnum.HighDueIn2Week: {
      applyFilter("due_date", ["Overdue or due in 2 weeks"]);
      applyFilter("risk_level", ["HIGH"]);
      await applyExcludingStatusFilter(
        gridRef,
        "workflow_status",
        excludeCompletedStatuses
      );
      break;
    }
    case FilterNamesEnum.SCHighDueIn2Week: {
      applyFilter("due_date", ["Overdue or due in 2 weeks"]);
      applyFilter("risk_level", ["HIGH"]);
      await applyExcludingStatusFilter(
        gridRef,
        "workflow_status",
        excludeCompletedStatuses
      );
      if (additionalData?.loggedInUserCompany) {
        applyFilter("trade_partner", [additionalData.loggedInUserCompany]);
      }
      break;
    }
    case FilterNamesEnum.LowDueIn2Week: {
      applyFilter("due_date", ["Overdue or due in 2 weeks"]);
      applyFilter("risk_level", ["LOW", "MEDIUM"]);
      await applyExcludingStatusFilter(
        gridRef,
        "workflow_status",
        excludeCompletedStatuses
      );
      break;
    }
    case FilterNamesEnum.SCLowDueIn2Week: {
      applyFilter("due_date", ["Overdue or due in 2 weeks"]);
      applyFilter("risk_level", ["LOW", "MEDIUM"]);
      await applyExcludingStatusFilter(
        gridRef,
        "workflow_status",
        excludeCompletedStatuses
      );
      if (additionalData?.loggedInUserCompany) {
        applyFilter("trade_partner", [additionalData.loggedInUserCompany]);
      }
      break;
    }
    case FilterNamesEnum.HighDueLater: {
      applyFilter("due_date", ["due after 2 weeks"]);
      applyFilter("risk_level", ["HIGH"]);
      await applyExcludingStatusFilter(
        gridRef,
        "workflow_status",
        excludeCompletedStatuses
      );
      break;
    }
    case FilterNamesEnum.SCHighDueLater: {
      applyFilter("due_date", ["due after 2 weeks"]);
      applyFilter("risk_level", ["HIGH"]);
      await applyExcludingStatusFilter(
        gridRef,
        "workflow_status",
        excludeCompletedStatuses
      );
      if (additionalData?.loggedInUserCompany) {
        applyFilter("trade_partner", [additionalData.loggedInUserCompany]);
      }
      break;
    }
    case FilterNamesEnum.LowDueLater: {
      applyFilter("due_date", ["due after 2 weeks"]);
      applyFilter("risk_level", ["LOW", "MEDIUM"]);
      await applyExcludingStatusFilter(
        gridRef,
        "workflow_status",
        excludeCompletedStatuses
      );
      break;
    }
    case FilterNamesEnum.SCLowDueLater: {
      applyFilter("due_date", ["due after 2 weeks"]);
      applyFilter("risk_level", ["LOW", "MEDIUM"]);
      await applyExcludingStatusFilter(
        gridRef,
        "workflow_status",
        excludeCompletedStatuses
      );
      if (additionalData?.loggedInUserCompany) {
        applyFilter("trade_partner", [additionalData.loggedInUserCompany]);
      }
      break;
    }
    case FilterNamesEnum.NoPlan:
      applyFilter("final_deadline_date", [BLANKS_FILTER_KEY]);
      await applyExcludingStatusFilter(
        gridRef,
        "workflow_status",
        excludeCompletedStatuses
      );
      break;
    case FilterNamesEnum.AIInsightsFieldMeasurementRequired: {
      applyFilter("ai_insights", ["Field Measurement Required"]);
      break;
    }
    case FilterNamesEnum.AIInsightsMockupsRequired: {
      applyFilter("ai_insights", ["Mockups Required"]);
      break;
    }
    case FilterNamesEnum.AIInsightsMockupsProof: {
      applyFilter("ai_insights", ["Mockups Proof"]);
      break;
    }
    case FilterNamesEnum.AIInsightsRecessedCondition: {
      applyFilter("ai_insights", ["Recessed Condition"]);
      break;
    }
    case FilterNamesEnum.LinkedToDeletedActivities: {
      applyFilter("linked_tasks", [
        LINKED_ACTIVITIES_COLUMN_FILTER_LABELS.DELETED
      ]);
      break;
    }
    default:
  }
  notifyFilterChanged(gridRef);
}

export function applyBidPackagesLogFilters(params: {
  gridRef: React.RefObject<AgGridReact<any>>;
  bidPackageListFilter: any;
}) {
  const { gridRef, bidPackageListFilter } = params;
  if (!gridRef.current) return;

  const applyFilter = createApplyFilter(gridRef);

  switch (bidPackageListFilter?.key) {
    case FilterNamesEnum.DueIn2Week:
      applySortByNextDeadline(gridRef);
      applyFilter("next_planned_deadline", ["Overdue or due in 2 weeks"]);
      break;
    default:
  }

  notifyFilterChanged(gridRef);
}
